import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { fetchSocialAccounts } from "../../../_actions/socialAccountsActions";
import { trackBasicEvent } from "../../../_utils/eventsTracker";
import PostPropType from "../../../_types/Post";
import * as PostEditorModes from "../PostEditor/PostEditorModes.constants";
import PostEditor from "../PostEditor/PostEditor";
import SocialAccountsList from "../SocialAccountsList/SocialAccountsList";
import Preloader from "../Preloader/Preloader";
import SocialAccountAvatar from "../SocialAccountAvatar/SocialAccountAvatar";

const PostEditorView = ({
  addConnectionUrl,
  editorHeading,
  defaultAccountsIds,
  defaultPostData,
  addPostMedia,
  onAccountsIdsChange,
  onActionDone,
  onAddConnectionClick,
  onCustomAction,
  mode = PostEditorModes.CREATE,
  postDataStoreKey,
  hideAccountsSelector,
  customHideTimeSelector,
  selectedAccountsReqired,
}) => {
  const { t } = useTranslation(["planner"]);
  const dispatch = useDispatch();
  const socialAccounts = useSelector(
    (state) => state.socialAccountsStore.accounts
  );
  const canEditDrafts = useSelector(
    (state) => state.permissions.workspacePermissionChecks.canEditDrafts
  );
  const canEditPosts = useSelector(
    (state) => state.permissions.workspacePermissionChecks.canEditPosts
  );
  const [accountsIds, setAccountsIds] = useState(
    defaultAccountsIds || defaultPostData?.accounts || []
  );
  const [postData, setPostData] = useState(defaultPostData);
  const [editorLoading, setEditorLoading] = useState(false);
  const [accountsFetched, setAccountsFetched] = useState(false);

  const singleMode = useMemo(
    () =>
      [
        PostEditorModes.EDIT,
        PostEditorModes.CUSTOM_EDIT,
        PostEditorModes.DRAFT_EDIT,
      ].includes(mode),
    [mode]
  );

  const handleAccountsChange = useCallback(
    (ids) => {
      setAccountsIds(ids);
      onAccountsIdsChange?.(ids);

      trackBasicEvent(
        "post-editor",
        "click",
        "account list - selection change"
      );
    },
    [setAccountsIds, onAccountsIdsChange]
  );

  const handleEditorLoading = useCallback(
    (state) => setEditorLoading(state),
    [setEditorLoading]
  );

  const handleEditorActionDone = useCallback(
    (...actionProps) => {
      setPostData({ message: "" }); // can't be an empty object because then will be data restored from sessionStorage
      onActionDone?.(...actionProps);
    },
    [setPostData, onActionDone]
  );

  const getSocialAccounts = useCallback(() => {
    setAccountsFetched(false);

    dispatch(fetchSocialAccounts()).then(() => {
      setAccountsFetched(true);
    });
  }, [dispatch, setAccountsFetched]);

  useEffect(() => {
    setAccountsIds(defaultAccountsIds);
  }, [defaultAccountsIds]);

  useEffect(() => {
    if (socialAccounts?.length) {
      setAccountsFetched(true);

      return;
    }
    getSocialAccounts();
  }, []);

  const renderSocialAccount = (item) => {
    if (!item) {
      return null;
    }

    return (
      <Box display="flex" alignItems="center">
        <Box marginRight="4">
          <SocialAccountAvatar
            as={Box}
            name={item.name}
            src={item.image}
            socialType={item.social_type}
            socialTypeShown
            size="lg"
          />
        </Box>
        <Box flex="1" minWidth="0">
          <Text fontSize="sm" fontWeight="bold" isTruncated title={item.name}>
            {item.name}
          </Text>
          <Text fontSize="xs" color="gray.300">
            {t(`social_types.${item.social_type}.full`)}
          </Text>
        </Box>
      </Box>
    );
  };

  return (
    <Box color="white">
      {editorHeading && socialAccounts?.length > 0 && (
        <Heading as="h1" size="md" ml="5" mb="4">
          {editorHeading}
        </Heading>
      )}
      {!hideAccountsSelector &&
        singleMode &&
        (!accountsFetched || socialAccounts) ? (
        <Box mx="3" mb="4">
          {accountsFetched ? (
            renderSocialAccount(
              socialAccounts?.find((item) => item.id === accountsIds[0])
            )
          ) : (
            <Preloader size="md" mb="4" />
          )}
        </Box>
      ) : null}

      {!singleMode && (
        <Box mt="-3" mx="2" p="2" maxHeight="8rem" overflowY="auto">
          {accountsFetched ? (
            <>
              {socialAccounts?.length ? (
                <SocialAccountsList
                  selected={accountsIds}
                  accounts={socialAccounts}
                  multiple={
                    mode === PostEditorModes.CREATE ||
                    mode === PostEditorModes.DRAFT_CREATE
                  }
                  onChange={handleAccountsChange}
                  disabled={!!editorLoading}
                  addConnectionUrl={addConnectionUrl}
                  required={selectedAccountsReqired}
                />
              ) : (
                <>
                  <Flex justifyContent="center" mb="4">
                    <Text size="sm" color="gray.200">
                      {t("accounts.add_accounts_to_start")}
                    </Text>
                  </Flex>
                  <Flex justifyContent="center" mb="4">
                    {addConnectionUrl && (
                      <Button
                        as="a"
                        variant="solid"
                        href={addConnectionUrl}
                        onClick={onAddConnectionClick}
                        size="sm"
                        target="_blank"
                      >
                        {t("accounts.add")}
                      </Button>
                    )}
                  </Flex>
                </>
              )}
            </>
          ) : (
            <Preloader size="md" mb="3" />
          )}
        </Box>
      )}
      {socialAccounts?.length ? (
        <Box>
          <PostEditor
            accounts={socialAccounts || []}
            accountsIds={singleMode ? accountsIds?.slice(0, 1) : accountsIds}
            canEditDrafts={canEditDrafts}
            canEditPosts={canEditPosts}
            postData={postData}
            addPostMedia={addPostMedia}
            mode={mode}
            dataStoreKey={postDataStoreKey}
            customHideTimeSelector={customHideTimeSelector}
            onLoading={handleEditorLoading}
            onActionDone={handleEditorActionDone}
            onCustomAction={onCustomAction}
          />
        </Box>
      ) : null}
    </Box>
  );
};

PostEditorView.propTypes = {
  addConnectionUrl: PropTypes.string,
  onAddConnectionClick: PropTypes.func,
  defaultAccountsIds: PropTypes.arrayOf(PropTypes.string.isRequired),
  defaultPostData: PostPropType,
  addPostMedia: PropTypes.arrayOf(PropTypes.shape({})),
  editorHeading: PropTypes.string,
  onAccountsIdsChange: PropTypes.func,
  onActionDone: PropTypes.func,
  onCustomAction: PropTypes.func,
  mode: PropTypes.oneOf(Object.values(PostEditorModes)),
  postDataStoreKey: PropTypes.string,
  hideAccountsSelector: PropTypes.bool,
  customHideTimeSelector: PropTypes.bool,
};

PostEditorView.defaultProps = {
  addConnectionUrl: undefined,
  defaultAccountsIds: undefined,
  defaultPostData: undefined,
  addPostMedia: undefined,
  editorHeading: undefined,
  onAccountsIdsChange: undefined,
  onActionDone: undefined,
  onAddConnectionClick: undefined,
  onCustomAction: undefined,
  mode: undefined,
  postDataStoreKey: undefined,
  hideAccountsSelector: undefined,
  customHideTimeSelector: undefined,
};

export default PostEditorView;
